import { useState, useEffect } from 'react'
import { WrapperCard } from '@/components/fragments'
import DashboardLayout from '../app-layout'
import { PaymentRevenueCard, PaymentTrendsChart } from './components'
import { PaymentsDefaultIcon, PoundCircleIcon, RefundIcon } from '@/assets/svg'
import routesPath from '@/utils/routesPath'
import { useNavigate } from 'react-router-dom'
import { useGetPaymentStatsQuery } from '@/redux/services/payments'
import { numberFormat } from '@/utils/helpers'

const PaymentsPage = () => {
  const navigate = useNavigate()
  const [trendPeriod, setTrendPeriod] = useState<'weekly' | 'monthly'>('weekly')
  const {
    data: paymentStats,
    isLoading: isLoadingStats,
    isFetching: isFetchingStats,
  } = useGetPaymentStatsQuery({ trend_period: trendPeriod })

  const stats = paymentStats?.statistics

  // Sync trend period with data when it changes
  useEffect(() => {
    if (stats?.payment_trends?.period) {
      setTrendPeriod(stats.payment_trends.period)
    }
  }, [stats?.payment_trends?.period])

  return (
    <DashboardLayout
      pageTitle='Payments Analytics'
      onClick={() => navigate(routesPath.PAYMENTS_LIST)}
      onClickText='Payments List'
      icon={PaymentsDefaultIcon}
    >
      <section className='w-full h-fit grid sm:grid-cols-2 md:grid-cols-3 gap-6'>
        <div className='w-full sm:col-span-2 md:col-span-2 h-[172px]'>
          <PaymentRevenueCard
            revenueToday={stats?.payment_revenue?.revenue_today}
            revenueThisWeek={stats?.payment_revenue?.revenue_this_week}
            loading={isLoadingStats}
          />
        </div>
        <WrapperCard
          title='Total Refunds'
          icon={RefundIcon}
          headerClassName='justify-between flex-row items-center gap-x-2'
          wrapperClassName='px-0 p-5 h-[172px]'
          loading={isLoadingStats}
        >
          <p className='SFProMedium text-[1.3rem] text-[#000000] font-bold'>
            {stats?.payment_revenue?.total_refunds?.formatted || '£0.00'}
          </p>
        </WrapperCard>

        <WrapperCard
          title='Total Revenue'
          icon={PoundCircleIcon}
          headerClassName='justify-between flex-row items-center gap-x-2'
          wrapperClassName='p-5 h-[172px]'
          total={
            stats?.overall_summary?.total_revenue?.formatted || numberFormat(0)
          }
          trend={stats?.overall_summary?.total_revenue?.change_description}
          trendType={stats?.overall_summary?.total_revenue?.change_type}
          isTrend={true}
          loading={isLoadingStats}
        />
        <WrapperCard
          title='Total Payments'
          icon={PoundCircleIcon}
          headerClassName='justify-between flex-row items-center gap-x-2'
          wrapperClassName='p-5 h-[172px]'
          total={stats?.overall_summary?.total_payments?.formatted || '0'}
          trend={stats?.overall_summary?.total_payments?.change_description}
          trendType={stats?.overall_summary?.total_payments?.change_type}
          isTrend={true}
          loading={isLoadingStats}
        />
        <WrapperCard
          title='Successful Payments'
          icon={PaymentsDefaultIcon}
          headerClassName='justify-between flex-row items-center gap-x-2'
          wrapperClassName='p-5 h-[172px]'
          total={stats?.overall_summary?.successful_payments?.formatted || '0'}
          trend={`${stats?.overall_summary?.successful_payments?.success_rate_formatted} success rate`}
          isTrend={true}
          loading={isLoadingStats}
        ></WrapperCard>
      </section>
      <div className='grid sm:grid-cols-2 md:grid-cols-2 gap-6 my-6'>
        <WrapperCard
          title='Pending Payments'
          icon={PoundCircleIcon}
          headerClassName='justify-between flex-row items-center gap-x-2'
          wrapperClassName='p-5 h-[172px]'
          total={stats?.status_breakdown?.pending_payments?.formatted || '0'}
          isTrend={true}
          loading={isLoadingStats}
        />

        <WrapperCard
          title='Failed Payments'
          icon={PaymentsDefaultIcon}
          headerClassName='justify-between flex-row items-center gap-x-2'
          wrapperClassName='p-5 h-[172px]'
          total={stats?.status_breakdown?.failed_payments?.formatted || '0'}
          trend={`${stats?.status_breakdown?.failed_payments?.failure_rate_formatted} failure rate`}
          isTrend={true}
          loading={isLoadingStats}
        />
      </div>
      <PaymentTrendsChart
        paymentTrends={stats?.payment_trends}
        loading={isLoadingStats || isFetchingStats}
        trendPeriod={trendPeriod}
        onTrendPeriodChange={setTrendPeriod}
      />
    </DashboardLayout>
  )
}

export default PaymentsPage
